import { useState } from "react";
import type { Drama } from "@/mocks/dramas";
import { siteInfo } from "@/mocks/dramas";

export default function DramaBody({ drama }: { drama: Drama }) {
  const [expandEpisodes, setExpandEpisodes] = useState(false);
  const episodes = Array.from({ length: drama.episodes }, (_, i) => i + 1);
  const visibleEpisodes = expandEpisodes ? episodes : episodes.slice(0, 12);

  return (
    <div className="mx-auto max-w-page px-4 py-14 md:px-6 md:py-16">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
        <div>
          <section aria-labelledby="synopsis-title">
            <h2 id="synopsis-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              剧情简介
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">{drama.synopsis}</p>
          </section>

          <section className="mt-12" aria-labelledby="highlights-title">
            <h2 id="highlights-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              本剧看点
            </h2>
            <ul className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              {drama.highlights.map((item, index) => (
                <li
                  key={item}
                  className="rounded-lg border border-background-200/70 bg-background-100/60 p-5"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 font-heading text-sm font-bold text-accent-400">
                    {index + 1}
                  </span>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-800">{item}</p>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12" aria-labelledby="episodes-title">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 id="episodes-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                分集速览
              </h2>
              <span className="text-xs font-label text-foreground-500">共 {drama.episodes} 集</span>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {visibleEpisodes.map((ep) => (
                <span
                  key={ep}
                  className="rounded-md border border-background-300/60 bg-background-100/60 px-3 py-2 text-xs font-label text-foreground-700"
                >
                  第 {ep} 集
                </span>
              ))}
            </div>
            {episodes.length > 12 && (
              <button
                type="button"
                onClick={() => setExpandEpisodes((v) => !v)}
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap text-sm font-label text-primary-400 transition-colors hover:text-primary-300 cursor-pointer"
              >
                {expandEpisodes ? "收起分集" : `展开全部 ${drama.episodes} 集`}
                <i className={expandEpisodes ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true"></i>
              </button>
            )}
          </section>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">剧集信息</h2>
            <dl className="mt-4 space-y-3 text-sm">
              {[
                { label: "类型", value: drama.category },
                { label: "状态", value: drama.status },
                { label: "集数", value: `${drama.episodes} 集` },
                { label: "上线年份", value: `${drama.year} 年` },
                { label: "标签", value: drama.tags.join(" · ") },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-4 border-b border-background-200/60 pb-3 last:border-0 last:pb-0">
                  <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-right font-label text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-lg border border-accent-500/30 bg-accent-100/20 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-information-line text-accent-400" aria-hidden="true"></i>
              观看提示
            </h2>
            <p className="mt-3 text-xs leading-relaxed text-foreground-700">
              单集时长约 2–3 分钟，建议按顺序观看。若你偏好强情节，可从第 1 集连看到第 10 集再决定是否续追。
            </p>
            <p className="mt-3 flex items-center gap-2 text-xs font-label text-foreground-500">
              <i className="ri-refresh-line text-accent-400" aria-hidden="true"></i>
              {siteInfo.updateNote}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}